import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Center, Group, Loader, Modal, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Application {
  id: number;
  job_id: number;
  job_title: string;
  company_name: string;
  location: string;
  status: string;
  applied_at: string;
  resume_url?: string;
  is_withdrawn?: boolean;
  withdraw_count?: number;
  modified_by_applicant?: boolean;
}

const MyApplicationsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<number | null>(null);
  const [confirmingApp, setConfirmingApp] = useState<Application | null>(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getMyApplications();
      // Handle paginated response
      const appData = response;
      setApplications(appData);
    } catch (error) {
      setError(error instanceof Error ? error.message : t('applications.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (appId: number) => {
    try {
      setWithdrawingId(appId);
      const updated = await api.withdrawApplication(appId);
      setApplications((prev) =>
        prev.map((app) => (app.id === appId ? { ...app, ...updated } : app))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('applications.withdrawError'));
    } finally {
      setWithdrawingId(null);
    }
  };

  const handleConfirmWithdraw = async () => {
    if (!confirmingApp) return;
    try {
      setWithdrawingId(confirmingApp.id);
      const updated = await api.withdrawApplication(confirmingApp.id, true);
      setApplications((prev) =>
        prev.map((app) => (app.id === confirmingApp.id ? { ...app, ...updated } : app))
      );
      setConfirmingApp(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('applications.withdrawError'));
    } finally {
      setWithdrawingId(null);
    }
  };

  if (user?.user_type === 'JOB_POSTER') {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Title order={3}>{t('applications.forPosters')}</Title>
        <Text c="dimmed">{t('applications.manageHint')}</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="sm">
        <Title order={2}>{t('applications.title')}</Title>
        <Text c="dimmed">{t('applications.subtitle')}</Text>
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {error}
          </Alert>
        )}
        {applications.length === 0 && <Text c="dimmed">No applications yet</Text>}
        <Stack gap="sm" mt="sm">
          {applications.map((app) => (
            <Card key={app.id} withBorder radius="md" shadow="xs" padding="md">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <div>
                  <Title order={5}>{app.job_title}</Title>
                  <Text c="dimmed" size="sm">{app.company_name} • {app.location}</Text>
                  <Text c="dimmed" size="xs" mt={4}>Applied: {new Date(app.applied_at).toLocaleDateString()}</Text>
                  {app.is_withdrawn && (
                    <Text c="dimmed" size="xs" mt={4}>
                      {t('applications.withdrawnByApplicant')}
                    </Text>
                  )}
                </div>
                <Group gap="xs" wrap="wrap">
                  {app.resume_url && (
                    <Button
                      size="xs"
                      variant="light"
                      component="a"
                      href={app.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('jobDetail.download')}
                    </Button>
                  )}
                  {!app.is_withdrawn && (
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => {
                        if ((app.withdraw_count || 0) >= 1) {
                          setConfirmingApp(app);
                        } else {
                          handleWithdraw(app.id);
                        }
                      }}
                      loading={withdrawingId === app.id}
                    >
                      {t('applications.withdraw')}
                    </Button>
                  )}
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>
      <Modal
        opened={Boolean(confirmingApp)}
        onClose={() => setConfirmingApp(null)}
        title={t('applications.withdrawFinalTitle')}
        size="sm"
      >
        <Stack gap="md">
          <Text>{t('applications.withdrawFinalMessage')}</Text>
          <Group justify="flex-end" gap="sm" wrap="wrap">
            <Button variant="subtle" onClick={() => setConfirmingApp(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              onClick={handleConfirmWithdraw}
              loading={withdrawingId === confirmingApp?.id}
            >
              {t('applications.withdrawFinalConfirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
};

export default MyApplicationsPage;
